<script lang="ts">
  import { ExternalLink } from 'lucide-svelte';
  
  export let title: string;
  export let description: string;
  export let technologies: string[];
  export let url: string = '';
  export let image: string = '';
  
  let cardElement: HTMLElement;
  let isHovering = false;
  
  function handleMouseMove(event: MouseEvent) {
    if (!cardElement) return;

    if (!isHovering) {
      isHovering = true;
    } else {
      cardElement.style.transition = 'none';
    }
    
    const rect = cardElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseXRelative = event.clientX - rect.left;
    const mouseYRelative = event.clientY - rect.top;
    
    const mouseX = (mouseXRelative / rect.width) * 100;
    const mouseY = (mouseYRelative / rect.height) * 100;
    
    const rotateX = (event.clientY - centerY) / 100;
    const rotateY = (centerX - event.clientX) / 100;
    
    cardElement.style.setProperty('--mouse-x', `${mouseX}%`);
    cardElement.style.setProperty('--mouse-y', `${mouseY}%`);
    cardElement.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px)`;
  }
  
  function handleMouseLeave() {
    if (!cardElement) return;
    isHovering = false;
    cardElement.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    cardElement.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
  }
</script>

<div
  bind:this={cardElement}
  class="glass-card project-card"
  on:mousemove={handleMouseMove}
  on:mouseleave={handleMouseLeave}
  role="article"
>
  {#if image}
    <div class="project-image">
      <img src={image} alt={title} />
    </div>
  {/if}
  
  <div class="project-content">
    <h3>{title}</h3>
    <p>{description}</p>
    
    <div class="technologies">
      {#each technologies as tech}
        <span class="tech-tag">{tech}</span>
      {/each}
    </div>
    
    <div class="project-links">
      {#if url}
        <a href={url} target="_blank" rel="noopener noreferrer" class="btn">
          <ExternalLink size={16} />
          View Project
        </a>
      {/if}
    </div>
  </div>
</div>

<style>
  .project-card {
    padding: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .project-image {
    height: 200px;
    overflow: hidden;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }
  
  .project-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .project-content {
    padding: var(--spacing-lg);
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .project-content h3 {
    margin-bottom: var(--spacing-sm);
  }
  
  .project-content p {
    color: var(--text-secondary);
    margin-bottom: var(--spacing-md);
    flex: 1;
  }
  
  .technologies {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    margin-bottom: var(--spacing-lg);
  }
  
  .tech-tag {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    background: rgba(90, 209, 151, 0.1);
    border: 1px solid rgba(90, 209, 151, 0.25);
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--accent-orange);
  }
  
  .project-links {
    display: flex;
    gap: var(--spacing-sm);
    margin-top: auto;
  }
  
</style>